// 用户服务层 - 统一管理用户获取，使用 React Query 缓存
import { QueryClient } from '@tanstack/react-query';
import { supabase } from '@ext-bookmark/shared/api';
import type { User } from '@supabase/supabase-js';

/** 未登录时 Supabase 会报此错误，属正常状态，不按错误打印 */
function isSessionMissingError(error: unknown): boolean {
  return (error as { name?: string })?.name === 'AuthSessionMissingError';
}

// 全局 queryClient 引用（在 App.tsx 中设置）
let globalQueryClient: QueryClient | null = null;

/**
 * 设置全局 QueryClient 实例
 * 在 App.tsx 中调用，确保所有服务都能访问 React Query 缓存
 */
export function setGlobalQueryClient(queryClient: QueryClient) {
  globalQueryClient = queryClient;
}

/**
 * 获取当前用户（优先从 React Query 缓存获取）
 * 这是所有服务应该使用的统一用户获取接口
 */
export async function getCurrentUser(): Promise<User | null> {
  if (!supabase) {
    return null;
  }

  // 1. 优先从 React Query 缓存获取
  if (globalQueryClient) {
    const cachedUser = globalQueryClient.getQueryData<User | null>(['auth', 'user']);
    if (cachedUser) {
      return cachedUser;
    }

    // 2. 如果缓存中没有，检查是否有正在进行的查询
    const queryState = globalQueryClient.getQueryState(['auth', 'user']);
    if (queryState?.status === 'pending') {
      // 等待查询完成（不触发新查询）
      // React Query 会自动去重，多个组件调用同一个查询时只会执行一次
      try {
        // 使用 fetchQuery，如果查询正在进行中，会等待现有查询完成
        // 如果查询不存在，才会执行 queryFn
        const result = await globalQueryClient.fetchQuery({
          queryKey: ['auth', 'user'],
          queryFn: async (): Promise<User | null> => {
            try {
              if (!supabase) return null;
              const { data, error } = await supabase.auth.getUser();
              if (error) {
                if (!isSessionMissingError(error)) {
                  console.error('[userService] 获取用户失败:', error);
                }
                return null;
              }
              return data.user;
            } catch (err) {
              if (!isSessionMissingError(err)) {
                console.error('[userService] 获取用户失败:', err);
              }
              return null;
            }
          },
          staleTime: Infinity,
        });
        return result;
      } catch (error) {
        if (!isSessionMissingError(error)) {
          console.error('[userService] 等待查询完成失败:', error);
        }
        // 如果等待失败，继续执行兜底方案
      }
    } else if (!queryState) {
      // 3. 如果查询不存在（这种情况应该很少，因为 AuthProvider 应该在应用启动时就执行了查询）
      // 触发一次查询，但 React Query 会自动去重
      try {
        const result = await globalQueryClient.fetchQuery({
          queryKey: ['auth', 'user'],
          queryFn: async (): Promise<User | null> => {
            if (!supabase) return null;
            const { data, error } = await supabase.auth.getUser();
            if (error) {
              console.error('[userService] 获取用户失败:', error);
              return null;
            }
            return data.user;
          },
          staleTime: Infinity,
        });
        return result;
      } catch (error) {
        console.error('[userService] 获取用户失败:', error);
        // 继续执行兜底方案
      }
    }
  }

  // 4. 如果 React Query 不可用，直接调用 API（兜底方案）
  // 这种情况应该很少，因为 App.tsx 已经设置了 globalQueryClient
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      if (!isSessionMissingError(error)) {
        console.error('[userService] 获取用户失败:', error);
      }
      return null;
    }
    return data.user;
  } catch (error) {
    if (!isSessionMissingError(error)) {
      console.error('[userService] 获取用户异常:', error);
    }
    return null;
  }
}

/**
 * 同步获取当前用户（从缓存，不等待）
 * 适用于不需要等待的场景
 */
export function getCurrentUserSync(): User | null {
  if (!globalQueryClient) {
    return null;
  }
  return globalQueryClient.getQueryData<User | null>(['auth', 'user']) || null;
}
